import { useEffect, useState } from 'react';
// Forecasting is GLOBAL data shared across sites — read from forecastDb (the
// producing project's Firestore; aliases the primary db on the chadha build).
// Note: `requestMapWindowRender` WRITES to `swellMapRenders`, which only the
// producing site's users are permitted to do — on satellite sites the
// on-demand history render is unavailable and fails gracefully.
import firebase, { forecastDb as db } from '../firebaseConfig';

// Client access to the swell source archive (see backend/api/swellArchive.js).
//
// The backend banks the settled (past) portion of every worker run into growing
// per-month shards on S3 (full-res grid `.bin.gz` + `.json` sidecar for basins,
// hourly `.json.gz` bundles for spots) and lists them in Firestore manifests
// (`swellArchive/{basinId}`, `swellArchiveSpots/{spotId}`). These helpers read a
// manifest, fetch + slice the shards covering an arbitrary window, and return
// data shaped exactly like the live decoders so the globe/forecast just consume
// it. The past-window MAP video is rendered on demand (requestMapWindowRender).

const ready = () => firebase.apps.length > 0 && db;
const gridCache = new Map(); // `${basinId}:${start}:${end}` -> Promise<grid>
const spotCache = new Map();

// Span of one history window. ~70d hindcast + 16d forecast on the live map ≈ 86.
export const WINDOW_DAYS = 86;
export const WINDOW_SEC = WINDOW_DAYS * 86400;

// ---- manifests ----------------------------------------------------------------

// Live subscription to a basin's archive extent + shard list.
export function useBasinArchive(basinId) {
  const [state, setState] = useState({ manifest: null, loading: true });
  useEffect(() => {
    if (!ready() || !basinId) { setState({ manifest: null, loading: false }); return undefined; }
    const unsub = db.collection('swellArchive').doc(basinId).onSnapshot(
      (snap) => setState({ manifest: snap.exists ? { id: snap.id, ...snap.data() } : null, loading: false }),
      () => setState({ manifest: null, loading: false })
    );
    return () => unsub();
  }, [basinId]);
  return state;
}

export function useSpotArchive(spotId) {
  const [state, setState] = useState({ manifest: null, loading: true });
  useEffect(() => {
    if (!ready() || !spotId) { setState({ manifest: null, loading: false }); return undefined; }
    const unsub = db.collection('swellArchiveSpots').doc(spotId).onSnapshot(
      (snap) => setState({ manifest: snap.exists ? { id: snap.id, ...snap.data() } : null, loading: false }),
      () => setState({ manifest: null, loading: false })
    );
    return () => unsub();
  }, [spotId]);
  return state;
}

// ---- basin grid window --------------------------------------------------------

// Decode one shard's gzipped binary (already inflated by the browser via the
// object's Content-Encoding: gzip) into copied, aligned typed arrays.
function decodeShard(meta, buf) {
  const bl = meta.byteLayout || {};
  const slice = (sec, Ctor) => {
    if (!sec) return null;
    const bpe = Ctor === Uint16Array ? 2 : 1;
    return new Ctor(buf.slice(sec.offset, sec.offset + sec.length * bpe));
  };
  return {
    frameTimes: meta.frameTimes || [],
    gnx: meta.gnx,
    gny: meta.gny,
    cells: meta.cells,
    heights: slice(bl.heights, Uint16Array),
    periods: slice(bl.periods, Uint8Array),
    dirs: slice(bl.dirs, Uint8Array),
    windKt: slice(bl.windKt, Uint8Array),
    windDir: slice(bl.windDir, Uint8Array),
    cloudPct: slice(bl.cloudPct, Uint8Array),
    precipMm: slice(bl.precipMm, Uint8Array),
    storm: slice(bl.storm, Uint8Array),
  };
}

// Fetch + slice the basin archive into a decoded grid for [startSec,endSec],
// shaped identically to frontend/src/data/swellGrid.js's decodeSwellGrid output
// so SwellGlobe can render it directly. Returns null if nothing is archived yet.
export async function loadArchiveGrid(basinId, startSec, endSec) {
  if (!ready() || !basinId) return null;
  const key = `${basinId}:${Math.round(startSec)}:${Math.round(endSec)}`;
  if (gridCache.has(key)) return gridCache.get(key);

  const p = (async () => {
    const snap = await db.collection('swellArchive').doc(basinId).get();
    if (!snap.exists) return null;
    const man = snap.data();
    const shards = (man.shards || [])
      .filter((s) => s.lastTime >= startSec && s.firstTime <= endSec)
      .sort((a, b) => a.month.localeCompare(b.month));
    if (!shards.length) return null;

    let gnx = man.gnx || null;
    let gny = man.gny || null;
    let frameHours = 3;
    let hasWind = false;
    let hasCloud = false;
    let hasPrecip = false;
    let hasStorm = false;
    const frames = [];
    for (const sh of shards) {
      // eslint-disable-next-line no-await-in-loop
      const [metaRes, binRes] = await Promise.all([fetch(sh.metaUrl), fetch(sh.binUrl)]);
      if (!metaRes.ok || !binRes.ok) continue;
      // eslint-disable-next-line no-await-in-loop
      const meta = await metaRes.json();
      // eslint-disable-next-line no-await-in-loop
      const buf = await binRes.arrayBuffer();
      const dec = decodeShard(meta, buf);
      gnx = meta.gnx; gny = meta.gny; frameHours = meta.frameHours || frameHours;
      if (dec.windKt) hasWind = true;
      if (dec.cloudPct) hasCloud = true;
      if (dec.precipMm) hasPrecip = true;
      if (dec.storm) hasStorm = true;
      const cells = meta.cells;
      dec.frameTimes.forEach((t, i) => {
        if (t < startSec || t > endSec) return;
        const base = i * cells;
        frames.push({
          t,
          heights: dec.heights.subarray(base, base + cells),
          periods: dec.periods.subarray(base, base + cells),
          dirs: dec.dirs.subarray(base, base + cells),
          windKt: dec.windKt ? dec.windKt.subarray(base, base + cells) : null,
          windDir: dec.windDir ? dec.windDir.subarray(base, base + cells) : null,
          cloudPct: dec.cloudPct ? dec.cloudPct.subarray(base, base + cells) : null,
          precipMm: dec.precipMm ? dec.precipMm.subarray(base, base + cells) : null,
          storm: dec.storm ? dec.storm.subarray(base, base + cells) : null,
        });
      });
    }
    if (!frames.length || !gnx || !gny) return null;
    frames.sort((a, b) => a.t - b.t);

    const cells = gnx * gny;
    const gnframes = frames.length;
    const total = gnframes * cells;
    const heights = new Uint16Array(total).fill(65535);
    const periods = new Uint8Array(total).fill(255);
    const dirs = new Uint8Array(total).fill(255);
    const windKt = hasWind ? new Uint8Array(total).fill(255) : null;
    const windDir = hasWind ? new Uint8Array(total).fill(255) : null;
    const cloudPct = hasCloud ? new Uint8Array(total).fill(255) : null;
    const precipMm = hasPrecip ? new Uint8Array(total).fill(255) : null;
    const storm = hasStorm ? new Uint8Array(total).fill(255) : null;
    const frameTimes = new Array(gnframes);
    frames.forEach((fr, f) => {
      const dst = f * cells;
      heights.set(fr.heights, dst);
      periods.set(fr.periods, dst);
      dirs.set(fr.dirs, dst);
      if (hasWind && fr.windKt) { windKt.set(fr.windKt, dst); windDir.set(fr.windDir, dst); }
      if (hasCloud && fr.cloudPct) cloudPct.set(fr.cloudPct, dst);
      if (hasPrecip && fr.precipMm) precipMm.set(fr.precipMm, dst);
      if (hasStorm && fr.storm) storm.set(fr.storm, dst);
      frameTimes[f] = fr.t;
    });

    return {
      gnx, gny, gnframes,
      latMin: man.latMin, latMax: man.latMax, lonMin: man.lonMin, lonMax: man.lonMax,
      frameTimes,
      nowFrame: gnframes - 1, // history window → "now" is the latest frame
      frameHours,
      hasWind, hasCloud, hasPrecip, hasStorm,
      maxPowerKw: 0,
      maxWindKt: 0,
      sentinels: man.sentinels || { H_LAND: 65535, D_LAND: 255, P_LAND: 255, W_LAND: 255 },
      heights, periods, dirs, windKt, windDir, cloudPct, precipMm, storm,
    };
  })().catch((err) => { gridCache.delete(key); throw err; });

  gridCache.set(key, p);
  return p;
}

// ---- spot hourly window -------------------------------------------------------

// Fetch + slice the spot archive into a `cond`-shaped bundle (hours/rangeStart/
// rangeEnd/utcOffsetSec) the Forecast meteogram can render. Null if empty.
export async function loadSpotArchive(spotId, startSec, endSec) {
  if (!ready() || !spotId) return null;
  const key = `${spotId}:${Math.round(startSec)}:${Math.round(endSec)}`;
  if (spotCache.has(key)) return spotCache.get(key);

  const p = (async () => {
    const snap = await db.collection('swellArchiveSpots').doc(spotId).get();
    if (!snap.exists) return null;
    const man = snap.data();
    const shards = (man.shards || [])
      .filter((s) => s.lastTime >= startSec && s.firstTime <= endSec)
      .sort((a, b) => a.month.localeCompare(b.month));
    if (!shards.length) return null;

    const byT = new Map();
    let meta = null;
    for (const sh of shards) {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(sh.url);
      if (!res.ok) continue;
      // eslint-disable-next-line no-await-in-loop
      const data = await res.json();
      meta = meta || data;
      (data.hours || []).forEach((h) => {
        if (h.t >= startSec && h.t <= endSec) byT.set(h.t, h);
      });
    }
    const hours = Array.from(byT.values()).sort((a, b) => a.t - b.t);
    if (!hours.length) return null;
    return {
      status: 'ready',
      history: true,
      lat: man.lat, lng: man.lng,
      timezone: man.timezone || meta?.timezone || 'GMT',
      utcOffsetSec: man.utcOffsetSec ?? meta?.utcOffsetSec ?? 0,
      units: 'metric',
      rangeStart: hours[0].t,
      rangeEnd: hours[hours.length - 1].t,
      hours,
      daily: { sunrise: [], sunset: [] },
      tideExtremes: [],
    };
  })().catch((err) => { spotCache.delete(key); throw err; });

  spotCache.set(key, p);
  return p;
}

// ---- on-demand past-window map render ----------------------------------------

const renderDocId = (basinId, start, end) => `${basinId}_${Math.round(start)}_${Math.round(end)}`;

// Ensure a past-window map render exists and subscribe to it. Writes a queued
// request doc (deterministic id → dedupe/cache) and returns the live result
// { status:'queued'|'rendering'|'ready'|'error', ... }. `enabled=false` no-ops.
export function useMapWindowRender(basinId, startSec, endSec, enabled) {
  const [state, setState] = useState({ status: 'idle' });
  useEffect(() => {
    if (!enabled || !ready() || !basinId || !Number.isFinite(startSec) || !Number.isFinite(endSec)) {
      setState({ status: 'idle' });
      return undefined;
    }
    const id = renderDocId(basinId, startSec, endSec);
    const ref = db.collection('swellMapRenders').doc(id);
    let cancelled = false;
    setState({ status: 'queued' });

    // Queue the render if it isn't already done/in-flight.
    ref.get().then((snap) => {
      if (cancelled) return;
      const d = snap.exists ? snap.data() : null;
      if (!d || (d.status !== 'ready' && d.status !== 'rendering' && d.status !== 'queued')) {
        ref.set({
          basinId,
          startTime: Math.round(startSec),
          endTime: Math.round(endSec),
          status: 'queued',
          requestedAt: firebase.firestore.FieldValue.serverTimestamp(),
        }, { merge: true }).catch(() => {});
      }
    }).catch(() => {});

    const unsub = ref.onSnapshot(
      (snap) => { if (!cancelled) setState(snap.exists ? { ...snap.data() } : { status: 'queued' }); },
      () => { if (!cancelled) setState({ status: 'error', message: 'subscribe failed' }); }
    );
    return () => { cancelled = true; unsub(); };
  }, [basinId, startSec, endSec, enabled]);
  return state;
}
